import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BarChart3, TrendingUp, TrendingDown, DollarSign, Users, ShoppingCart, Store, Package, Eye, Euro, ChevronRight } from 'lucide-react';
import AnalyticsSystemSection from '../components/AnalyticsSystemSection';
import { VALID_SECTIONS } from '../utils/analyticsNavigation';
import { normalizeCommercantsResponse } from '../utils/commercantsList';
import { mapDashboardAnalytics, mapOverviewAnalytics, CHART_COLORS } from '../utils/analyticsDataMapper';
import { SITE_WEB_SEMANTICS_HINT } from '../utils/canalMetierLabels';
import { resolveMerchantOperationalScope } from '../utils/assistanceSession';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import apiService from '../services/api';
import AnalyticsDetailView from '../components/analytics/AnalyticsDetailView';
import AnalyticsInfoHint from '../components/analytics/AnalyticsInfoHint';
import AnalyticsPeriodSelector from '../components/analytics/AnalyticsPeriodSelector';
import { buildAnalyticsDetailPageModel } from '../components/analytics/buildAnalyticsDetailPageModel';
import { kpiHelp } from '../components/analytics/analyticsKpiDefinitions';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

// Fonction utilitaire pour formater les montants avec 2 décimales
const formatCurrency = (amount) => {
  return Number(amount || 0).toFixed(2).replace('.', ',') + '€';
};

// Fonction pour convertir période frontend en période backend
const convertPeriod = (period) => {
  const mapping = {
    '7j': '7d',
    '30j': '30d',
    '90j': '90d',
    '1a': '1a',
  };
  return mapping[period] || '30d';
};

const sectionClass = (activeSection, id) =>
  `scroll-mt-24 ${activeSection === id ? 'ring-2 ring-primary-200 ring-offset-2 rounded-lg' : ''}`;

const SA_DETAIL_KEYS = new Set([
  'ca',
  'commandes',
  'panier',
  'utilisateurs',
  'commercants',
  'trafic',
  'remboursements',
  'clients',
  'gift',
]);

const SA_SHOP_RICH_KEYS = new Set([
  'ca',
  'commandes',
  'panier',
  'remboursements',
  'trafic',
  'gift',
  'produits',
  'comportement',
]);

const fmtCount = (n) =>
  n == null || n === '' || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString('fr-FR');

function SaKpiShell({
  'data-testid': testId,
  onOpen,
  ariaLabel,
  footerClass,
  footer,
  clickable = true,
  extraClass = '',
  children,
}) {
  return (
    <div
      data-testid={testId}
      className={`card text-left w-full h-full flex flex-col relative ${
        clickable ? 'transition hover:shadow-md hover:border-gray-300 group cursor-pointer' : extraClass
      }`}
    >
      <div className="relative z-10 flex-1 pointer-events-none">{children}</div>
      <p
        className={`relative z-10 pointer-events-none mt-auto text-xs font-medium -mx-6 -mb-6 px-6 py-2.5 h-10 flex items-center border-t rounded-b-lg ${footerClass}`}
      >
        {footer}
      </p>
      {clickable ? (
        <button
          type="button"
          onClick={onOpen}
          className="absolute inset-0 z-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2"
          aria-label={ariaLabel}
        />
      ) : null}
    </div>
  );
}

const Analytics = () => {
  const navigate = useNavigate();
  const { detailKey: detailKeyParam } = useParams();
  const detailKey =
    detailKeyParam && SA_DETAIL_KEYS.has(detailKeyParam) ? detailKeyParam : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get('section');
  const initialPeriod = () => {
    if (searchParams.get('dateDebut') && searchParams.get('dateFin')) return 'custom';
    const p = searchParams.get('period');
    return p && ['7j', '30j', '90j', '1a'].includes(p) ? p : '30j';
  };

  const [analytics, setAnalytics] = useState({
    revenus: {
      total: 0,
      brut: 0,
      rembourse: 0,
      evolution: null,
      parJour: [],
      panierMoyen: 0,
      label: 'CA net ventes',
    },
    commandes: {
      total: 0,
      evolution: null,
      parStatut: [],
      parCanal: [],
      parPaiement: [],
      parJour: []
    },
    _meta: null,
    utilisateurs: {
      total: null,
      evolution: null,
      nouveaux: null
    },
    commercants: {
      total: null,
      actifs: null,
      topPerformers: []
    },
    produits: {
      total: 0,
      topVendus: []
    },
    vues: {
      globales: 0,
      commercants: 0,
      total: 0,
      moyenneParCommercant: null,
      topCommercants: [],
      evolution: []
    },
    conversion: {
      tauxVueVersCommande: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState(initialPeriod);
  const [customDateDebut, setCustomDateDebut] = useState(
    () => searchParams.get('dateDebut') || ''
  );
  const [customDateFin, setCustomDateFin] = useState(
    () => searchParams.get('dateFin') || ''
  );
  const [selectedMerchant, setSelectedMerchant] = useState(
    () => searchParams.get('commercantId') || 'all'
  );
  const [commercants, setCommercants] = useState([]);
  const [merchantOverview, setMerchantOverview] = useState(null);
  const scrolledRef = useRef(false);

  const syncUrl = useCallback(
    (overrides = {}) => {
      const next = new URLSearchParams(searchParams);
      const p = overrides.period ?? period;
      const debut = overrides.dateDebut ?? customDateDebut;
      const fin = overrides.dateFin ?? customDateFin;
      const merchant = overrides.commercantId ?? selectedMerchant;
      const section = overrides.section ?? activeSection;

      if (section && VALID_SECTIONS.includes(section)) {
        next.set('section', section);
      } else {
        next.delete('section');
      }

      if (merchant && merchant !== 'all') {
        next.set('commercantId', merchant);
      } else {
        next.delete('commercantId');
      }

      if (p === 'custom' && debut && fin) {
        next.set('period', 'custom');
        next.set('dateDebut', debut);
        next.set('dateFin', fin);
      } else {
        next.delete('dateDebut');
        next.delete('dateFin');
        next.set('period', p === 'custom' ? '30j' : p);
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, period, customDateDebut, customDateFin, selectedMerchant, activeSection, setSearchParams]
  );

  // Récupérer l'utilisateur actuel
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('admin_user');
      if (userData) {
        return JSON.parse(userData);
      }
    } catch (error) {
      console.error('Erreur parsing user data:', error);
    }
    return null;
  };

  // Initialiser selectedMerchant pour les commerçants / assistance
  useEffect(() => {
    const scope = resolveMerchantOperationalScope(getCurrentUser());
    const isCommercant = scope.scopedToMerchant;
    const userCommercantId = scope.commercantId;
    
    if (isCommercant && userCommercantId && selectedMerchant === 'all') {
      setSelectedMerchant(userCommercantId);
    }
  }, []);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (period === 'custom' && (!customDateDebut || !customDateFin)) {
        setLoading(false);
        setError('');
        return;
      }

      try {
        setLoading(true);
        setError('');
        
        const scope = resolveMerchantOperationalScope(getCurrentUser());
        const isCommercant = scope.scopedToMerchant;
        const userCommercantId = scope.commercantId;
        
        if (isCommercant && userCommercantId) {
          if (selectedMerchant === 'all') {
            setSelectedMerchant(userCommercantId);
            return;
          }
          
          try {
            const commercant = await apiService.getCommercant(userCommercantId);
            setCommercants(commercant ? [commercant] : []);
          } catch (err) {
            console.error('Erreur chargement commerçant:', err);
            setCommercants([]);
          }
        } else {
          const commercantsData = await apiService.getCommercants({
            pageSize: 500,
            includeInactive: 'true',
          });
          setCommercants(normalizeCommercantsResponse(commercantsData));
        }
        
        const periodBackend = convertPeriod(period === 'custom' ? '30j' : period);
        const useCustomRange = period === 'custom' && customDateDebut && customDateFin;
        const periodQuery = useCustomRange
          ? { dateDebut: customDateDebut, dateFin: customDateFin }
          : { period: period === 'custom' ? '30j' : period };

        const merchantId =
          isCommercant && userCommercantId ? userCommercantId : selectedMerchant;

        let mappedCore;
        let analyticsData;
        let overview = null;

        if (merchantId !== 'all') {
          overview = await apiService.getCommercantAnalyticsOverview(
            merchantId,
            periodQuery
          );
          if (overview?.error) {
            throw new Error(overview.error);
          }
          mappedCore = mapOverviewAnalytics(overview);
          analyticsData = overview;
        } else {
          analyticsData = await apiService.getAnalytics(periodQuery);
          mappedCore = mapDashboardAnalytics(analyticsData);
        }
        
        // Récupérer les données de vues (Super Admin — mode toutes boutiques uniquement).
        // En mode boutique : overview.trafic uniquement (page-views/stats preprod peut encore ignorer le filtre).
        let pageViewsData = null;
        try {
          const merchantIdForViews = (isCommercant && userCommercantId) ? userCommercantId : selectedMerchant;
          
          const viewsQuery = useCustomRange
            ? { dateDebut: customDateDebut, dateFin: customDateFin }
            : { period: periodBackend };

          if (merchantIdForViews === 'all') {
            pageViewsData = await apiService.getPageViewsStats(viewsQuery);
            console.log('✅ Page views data récupérées:', pageViewsData);
          }
        } catch (err) {
          console.warn('⚠️ Erreur récupération page views (peut-être pas SUPER_ADMIN):', err);
          // On continue avec les données de fallback depuis analytics dashboard
        }

        const totalCommandes = mappedCore.commandes.total;
        const totalRevenus = mappedCore.revenus.total;

        let vuesGlobales = 0;
        let vuesCommercants = 0;
        let totalVues = 0;
        let moyenneParCommercant = null;
        let topCommercants = [];
        let evolutionVues = [];

        if (merchantId === 'all') {
          vuesGlobales = pageViewsData?.vuesGlobales ?? analyticsData.totalVuesGlobales ?? 0;
          vuesCommercants = pageViewsData?.vuesCommercants ?? analyticsData.totalVuesCommercants ?? 0;
          totalVues = pageViewsData?.totalVues ?? vuesGlobales + vuesCommercants;
          moyenneParCommercant = pageViewsData?.moyenneParCommercant ?? null;
          topCommercants = pageViewsData?.topCommercants ?? [];
          evolutionVues = pageViewsData?.evolution ?? [];
        } else {
          // Boutique sélectionnée : overview.trafic est la seule source tenant-safe.
          // page-views/stats preprod peut encore renvoyer le total toutes boutiques.
          // Série : overview expose { date, vues, sessions } — pas vuesCommercants.
          const traficBoutique = overview?.trafic || analyticsData?.trafic || {};
          vuesCommercants = Number(traficBoutique.totalVues ?? 0);
          totalVues = vuesCommercants;
          evolutionVues = Array.isArray(traficBoutique.evolution)
            ? traficBoutique.evolution.map((row) => {
                const vues = Number(
                  row.vues ?? row.vuesCommercants ?? row.total ?? 0
                );
                return {
                  date: row.date,
                  vues,
                  sessions: Number(row.sessions ?? 0),
                  // Alias pour le Line chart (même champ que le mode global)
                  vuesCommercants: vues,
                  total: vues,
                };
              })
            : [];
        }

        const realAnalytics = {
          ...mappedCore,
          utilisateurs: {
            ...mappedCore.utilisateurs,
            total: analyticsData.totalUsers ?? mappedCore.utilisateurs.total,
          },
          commercants: {
            total: analyticsData.totalCommercants ?? mappedCore.commercants.total,
            actifs:
              analyticsData.commercantsActifs ?? mappedCore.commercants.actifs ?? null,
            topPerformers:
              merchantId === 'all' && pageViewsData?.topCommercants?.length > 0
                ? pageViewsData.topCommercants.map((item) => ({
                    nom: item.commercant?.nom || 'Commerçant inconnu',
                    logo: item.commercant?.logo || null,
                    vues: item.count || 0,
                    revenus: 0,
                    commandes: 0,
                  }))
                : [],
          },
          periodLabel: mappedCore.periodLabel || analyticsData.periodLabel || null,
          produits: {
            total:
              (analyticsData.totalProduits || 0) + (analyticsData.totalPrestations || 0) ||
              mappedCore.produits.topVendus.length,
            topVendus: mappedCore.produits.topVendus,
          },
          vues: {
            globales: vuesGlobales,
            commercants: vuesCommercants,
            total: totalVues,
            moyenneParCommercant,
            topCommercants,
            evolution: evolutionVues,
          },
          conversion: { tauxVueVersCommande: 0 },
          _meta: {
            merchantId,
            channelOrders: (mappedCore.commandes?.parCanal || []).reduce(
              (s, c) => s + Number(c.commandes || 0),
              0
            ),
            paymentOrders: (mappedCore.commandes?.parPaiement || []).reduce(
              (s, c) => s + Number(c.commandes || 0),
              0
            ),
            paidOrders: Number(mappedCore.commandes?.total || 0),
          },
        };

        if (merchantId !== 'all') {
          const paid = realAnalytics._meta.paidOrders;
          const ch = realAnalytics._meta.channelOrders;
          const pay = realAnalytics._meta.paymentOrders;
          if ((ch > 0 && paid > 0 && ch !== paid) || (pay > 0 && paid > 0 && pay !== paid)) {
            console.warn('[SA Analytics] Répartition ≠ volume payé boutique', {
              merchantId,
              paid,
              ch,
              pay,
            });
          }
        }

        const totalVuesPourConversion =
          realAnalytics.vues.total || realAnalytics.vues.globales + realAnalytics.vues.commercants;
        if (totalVuesPourConversion > 0 && totalCommandes > 0) {
          realAnalytics.conversion.tauxVueVersCommande =
            (totalCommandes / totalVuesPourConversion) * 100;
        }

        setAnalytics(realAnalytics);
        setMerchantOverview(merchantId !== 'all' ? overview : null);
      } catch (err) {
        setError('Erreur lors du chargement des analytics');
        console.error('Analytics error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [period, selectedMerchant, customDateDebut, customDateFin]);

  useEffect(() => {
    if (loading || !activeSection || scrolledRef.current) return;
    const el = document.getElementById(`analytics-section-${activeSection}`);
    if (el) {
      scrolledRef.current = true;
      window.setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [loading, activeSection]);

  useEffect(() => {
    scrolledRef.current = false;
  }, [activeSection]);

  const getEvolutionColor = (evolution) => {
    return evolution >= 0 ? 'text-green-600' : 'text-red-600';
  };

  const getEvolutionIcon = (evolution) => {
    return evolution >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />;
  };

  const awaitingCustomDates =
    period === 'custom' && (!customDateDebut || !customDateFin);

  const periodTitle =
    analytics.periodLabel ||
    (period === 'custom' && customDateDebut && customDateFin
      ? `${customDateDebut} → ${customDateFin}`
      : period === '7j'
        ? '7 derniers jours'
        : period === '30j'
          ? '30 derniers jours'
          : period === '90j'
            ? '90 derniers jours'
            : period === '1a'
              ? '1 an'
              : 'période sélectionnée');

  const activeMerchantsCount =
    analytics.commercants.actifs != null
      ? analytics.commercants.actifs
      : null;

  const openDetail = useCallback(
    (key) => {
      const qs = searchParams.toString();
      navigate(`/analytics/${key}${qs ? `?${qs}` : ''}`);
    },
    [navigate, searchParams]
  );

  const closeDetail = useCallback(() => {
    const qs = searchParams.toString();
    navigate(`/analytics${qs ? `?${qs}` : ''}`);
  }, [navigate, searchParams]);

  const saDetailModel = useMemo(() => {
    if (!detailKey) return null;
    const shopScoped = selectedMerchant !== 'all' && merchantOverview;
    const useMerchant = Boolean(shopScoped && SA_SHOP_RICH_KEYS.has(detailKey));
    return buildAnalyticsDetailPageModel({
      detailKey,
      mode: useMerchant ? 'merchant' : 'sa',
      overview: useMerchant
        ? { ...merchantOverview, commercantId: selectedMerchant }
        : null,
      saAnalytics: analytics,
      periodLabel: periodTitle,
    });
  }, [detailKey, analytics, periodTitle, selectedMerchant, merchantOverview]);

  if (loading && !awaitingCustomDates) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-600">Statistiques et analyses de performance</p>
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (detailKey) {
    return (
      <AnalyticsDetailView
        model={saDetailModel}
        onBack={closeDetail}
        evolutionChart={null}
        detailKey={detailKey}
        merchantName={
          selectedMerchant === 'all'
            ? 'Tous les commerçants'
            : commercants.find((c) => c.id === selectedMerchant)?.nom || null
        }
        periodControls={
          <div className="flex flex-col items-end gap-2">
            {(() => {
              const isCommercant = resolveMerchantOperationalScope(getCurrentUser()).scopedToMerchant;
              if (isCommercant) return null;
              return (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Boutique:</label>
                  <select
                    value={selectedMerchant}
                    onChange={(e) => {
                      setSelectedMerchant(e.target.value);
                      syncUrl({ commercantId: e.target.value });
                    }}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    data-testid="analytics-detail-merchant-select"
                  >
                    <option value="all">Toutes les boutiques</option>
                    {commercants.map((commercant) => (
                      <option key={commercant.id} value={commercant.id}>
                        {commercant.nom}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })()}
            <AnalyticsPeriodSelector
              periode={period}
              onPeriodChange={(p) => {
                setPeriod(p);
                if (p !== 'custom') syncUrl({ period: p });
                else setError('');
              }}
              customDateDebut={customDateDebut}
              customDateFin={customDateFin}
              onCustomDebutChange={(v) => {
                setCustomDateDebut(v);
                if (period === 'custom' && v && customDateFin) {
                  syncUrl({ period: 'custom', dateDebut: v, dateFin: customDateFin });
                }
              }}
              onCustomFinChange={(v) => {
                setCustomDateFin(v);
                if (period === 'custom' && customDateDebut && v) {
                  syncUrl({ period: 'custom', dateDebut: customDateDebut, dateFin: v });
                }
              }}
              tone="primary"
              testId="analytics-detail-period-selector"
            />
          </div>
        }
        performanceOverview={
          selectedMerchant !== 'all' ? merchantOverview || null : null
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">
            {(() => {
              const scope = resolveMerchantOperationalScope(getCurrentUser());
              const isCommercant = scope.scopedToMerchant;
              const userCommercantId = scope.commercantId;
              
              if (isCommercant && userCommercantId) {
                return 'Statistiques et analyses de performance de ma boutique';
              }
              
              return selectedMerchant === 'all' 
                ? 'Statistiques et analyses de performance globales' 
                : `Statistiques pour ${commercants.find(c => c.id === selectedMerchant)?.nom || 'la boutique sélectionnée'}`;
            })()}
          </p>
        </div>
        <div className="flex space-x-4">
          {/* Filtre par commerçant - Masqué pour les commerçants */}
          {(() => {
            const isCommercant = resolveMerchantOperationalScope(getCurrentUser()).scopedToMerchant;
            
            if (isCommercant) {
              return null; // Ne pas afficher le dropdown pour commerçant / assistance
            }
            
            return (
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Boutique:</label>
                <select
                  value={selectedMerchant}
                  onChange={(e) => {
                    setSelectedMerchant(e.target.value);
                    syncUrl({ commercantId: e.target.value });
                  }}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">Toutes les boutiques</option>
                  {commercants.map(commercant => (
                    <option key={commercant.id} value={commercant.id}>
                      {commercant.nom}
                    </option>
                  ))}
                </select>
              </div>
            );
          })()}
          
          {/* Filtre par période */}
          <AnalyticsPeriodSelector
            periode={period}
            onPeriodChange={(p) => {
              setPeriod(p);
              if (p !== 'custom') syncUrl({ period: p });
              else setError('');
            }}
            customDateDebut={customDateDebut}
            customDateFin={customDateFin}
            onCustomDebutChange={setCustomDateDebut}
            onCustomFinChange={setCustomDateFin}
            tone="primary"
          />
        </div>
      </div>

      {awaitingCustomDates && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Choisissez une date de début et de fin — la mise à jour est automatique.
        </div>
      )}

      {/* Métriques principales — cards cliquables, bandeaux bas alignés */}
      <div
        id="analytics-section-revenus"
        className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 items-stretch ${sectionClass(activeSection, 'revenus')}`}
        data-testid="analytics-sa-kpi-row"
      >
        <SaKpiShell
          data-testid="analytics-sa-kpi-ca"
          onOpen={() => openDetail('ca')}
          ariaLabel="CA net ventes — ouvrir le détail"
          footerClass="text-emerald-800 bg-emerald-50 border-emerald-100"
          footer="Voir le détail →"
        >
          <div className="flex items-start min-h-[5.5rem]">
            <div className="flex-shrink-0">
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                  <span className="truncate">CA net ventes</span>
                  <AnalyticsInfoHint text={kpiHelp('caNetVentes')} />
                </dt>
                <dd className="text-2xl font-semibold text-gray-900">
                  {formatCurrency(analytics.revenus.total)}
                </dd>
                {(analytics.revenus.brut > 0 || analytics.revenus.rembourse > 0) && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Brut {formatCurrency(analytics.revenus.brut)}
                    {analytics.revenus.rembourse > 0
                      ? ` − Remb. ${formatCurrency(analytics.revenus.rembourse)}`
                      : ''}
                  </p>
                )}
              </dl>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 shrink-0 mt-1" />
          </div>
        </SaKpiShell>

        <SaKpiShell
          data-testid="analytics-sa-kpi-commandes"
          onOpen={() => openDetail('commandes')}
          ariaLabel="Commandes — ouvrir le détail"
          footerClass="text-blue-800 bg-blue-50 border-blue-100"
          footer="Voir le détail →"
        >
          <div className="flex items-start min-h-[5.5rem]">
            <div className="flex-shrink-0">
              <ShoppingCart className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                  <span className="truncate">Commandes payées</span>
                  <AnalyticsInfoHint text={kpiHelp('commandesPayees')} />
                </dt>
                <dd className="text-2xl font-semibold text-gray-900">{fmtCount(analytics.commandes.total)}</dd>
              </dl>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 shrink-0 mt-1" />
          </div>
        </SaKpiShell>

        <SaKpiShell
          data-testid="analytics-sa-kpi-panier"
          onOpen={() => openDetail('panier')}
          ariaLabel="Panier moyen ventes — ouvrir le détail"
          footerClass="text-amber-900 bg-amber-50 border-amber-100"
          footer="Voir le détail →"
        >
          <div className="flex items-start min-h-[5.5rem]">
            <div className="flex-shrink-0">
              <Euro className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                  <span className="truncate">Panier moyen ventes</span>
                  <AnalyticsInfoHint text={kpiHelp('panierMoyen')} />
                </dt>
                <dd className="text-2xl font-semibold text-gray-900">
                  {formatCurrency(analytics.revenus.panierMoyen)}
                </dd>
                <p className="text-xs text-gray-500 mt-0.5">CA net / commandes payées</p>
              </dl>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 shrink-0 mt-1" />
          </div>
        </SaKpiShell>

        <SaKpiShell
          data-testid="analytics-sa-kpi-users"
          onOpen={() => openDetail('utilisateurs')}
          ariaLabel="Comptes plateforme — ouvrir le détail"
          footerClass="text-violet-800 bg-violet-50 border-violet-100"
          footer="Voir le détail →"
        >
          <div className="flex items-start min-h-[5.5rem]">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                  <span className="truncate">Comptes plateforme</span>
                  <AnalyticsInfoHint text={kpiHelp('comptesPlateforme')} />
                </dt>
                <dd className="text-2xl font-semibold text-gray-900">{fmtCount(analytics.utilisateurs.total)}</dd>
                <p className="text-xs text-gray-500 mt-0.5">
                  Total actuel — indépendant de la période · ≠ module Clients
                </p>
              </dl>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 shrink-0 mt-1" />
          </div>
        </SaKpiShell>

        {selectedMerchant === 'all' ? (
        <SaKpiShell
          data-testid="analytics-sa-kpi-commercants"
          onOpen={() => openDetail('commercants')}
          ariaLabel="Commerçants — ouvrir le détail"
          footerClass="text-orange-800 bg-orange-50 border-orange-100"
          footer="Voir le détail →"
        >
          <div className="flex items-start min-h-[5.5rem]">
            <div className="flex-shrink-0">
              <Store className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                  <span className="truncate">Commerçants (total)</span>
                  <AnalyticsInfoHint text={kpiHelp('commercants')} />
                </dt>
                <dd className="text-2xl font-semibold text-gray-900">{fmtCount(analytics.commercants.total)}</dd>
                <p className="text-xs text-gray-500 mt-0.5">
                  Total actuel — indépendant de la période
                  {activeMerchantsCount != null ? ` · dont ${activeMerchantsCount} actifs` : ''}
                </p>
              </dl>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 shrink-0 mt-1" />
          </div>
        </SaKpiShell>
        ) : (
        <SaKpiShell
          data-testid="analytics-sa-kpi-boutique-analysee"
          clickable={false}
          extraClass="border-orange-100 bg-orange-50/40"
          footerClass="text-orange-800/80 bg-orange-50 border-orange-100"
          footer="Filtre boutique actif"
        >
          <div className="flex items-start min-h-[5.5rem]">
            <div className="flex-shrink-0">
              <Store className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                  <span className="truncate">Boutique analysée</span>
                  <AnalyticsInfoHint text="Filtre Super Admin actif : les KPI CA / commandes / panier / vues / répartition portent sur cette boutique." />
                </dt>
                <dd className="text-lg font-semibold text-gray-900 truncate">
                  {commercants.find((c) => c.id === selectedMerchant)?.nom || 'Boutique'}
                </dd>
                <p className="text-xs text-gray-500 mt-0.5">Période : {periodTitle}</p>
              </dl>
            </div>
          </div>
        </SaKpiShell>
        )}
      </div>

      {/* Section Trafic & Vues */}
      <div
        id="analytics-section-traffic"
        className={`space-y-6 ${sectionClass(activeSection, 'traffic')}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-xl font-bold text-gray-900">
              {selectedMerchant === 'all' ? 'Trafic & Vues' : 'Vues de la boutique'}
            </h2>
            <AnalyticsInfoHint text={kpiHelp('pagesVues')} />
            <button
              type="button"
              data-testid="analytics-sa-kpi-trafic"
              onClick={() => openDetail('trafic')}
              className="text-xs font-medium text-sky-800 hover:text-sky-950 shrink-0"
            >
              Voir le détail →
            </button>
          </div>
            {(analytics.vues.total === 0 && analytics.vues.globales === 0 && analytics.vues.commercants === 0) && (
              <span className="text-sm text-gray-500 italic">
                (Les données de vues seront disponibles une fois que les pages commenceront à être visitées)
              </span>
            )}
          </div>
          
          {/* Métriques de vues — tracking plateforme global hors primary dashboard (POST_PROD) */}
          <div
            className={`grid grid-cols-1 gap-5 ${
              selectedMerchant === 'all' ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2'
            }`}
          >
            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Store className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                      {selectedMerchant === 'all' ? 'Vues Commerçants' : 'Vues de cette boutique'}
                      <AnalyticsInfoHint text={kpiHelp('vuesCommercants')} />
                    </dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {analytics.vues.commercants.toLocaleString('fr-FR')}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            {selectedMerchant === 'all' && (
              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BarChart3 className="h-8 w-8 text-purple-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                        <span className="truncate">Moyenne / boutique active</span>
                        <AnalyticsInfoHint text={kpiHelp('moyenneBoutiqueActive')} />
                      </dt>
                      <dd className="text-2xl font-semibold text-gray-900">
                        {fmtCount(analytics.vues.moyenneParCommercant)}
                      </dd>
                      <p className="text-xs text-gray-500 mt-1">
                        {activeMerchantsCount != null
                          ? `Vues commerçants ÷ ${activeMerchantsCount} boutiques actives`
                          : 'Dénominateur = boutiques actives (pas le total)'}
                      </p>
                    </dl>
                  </div>
                </div>
              </div>
            )}

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Eye className="h-8 w-8 text-sky-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                      Pages vues (total affiché)
                      <AnalyticsInfoHint text={kpiHelp('pagesVues')} />
                    </dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {analytics.vues.total.toLocaleString('fr-FR')}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Indicateur approximatif commandes/pages vues : masqué — non fiable pour décider.
              Conversion certifiée = Analytics commerçant (sessions PURCHASE / sessions boutique). */}

          {/* Graphique évolution des vues */}
          {analytics.vues.evolution.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Évolution des vues ({periodTitle})
              </h3>
              <div className="h-64">
                <Line
                  key={`vues-evol-${selectedMerchant}-${period}-${analytics.vues.evolution.map((i) => i.vues ?? i.vuesCommercants ?? 0).join('-')}`}
                  data={{
                    labels: analytics.vues.evolution.map(item => {
                      const date = new Date(item.date);
                      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                    }),
                    datasets: selectedMerchant === 'all' ? [
                      {
                        label: 'Vues Commerçants',
                        data: analytics.vues.evolution.map(item => item.vuesCommercants || 0),
                        borderColor: 'rgb(34, 197, 94)',
                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        tension: 0.4,
                        fill: true
                      }
                    ] : [
                      {
                        label: 'Vues de cette boutique',
                        data: analytics.vues.evolution.map(
                          (item) =>
                            Number(item.vues ?? item.vuesCommercants ?? item.total ?? 0)
                        ),
                        borderColor: 'rgb(34, 197, 94)',
                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        tension: 0.4,
                        fill: true
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true
                      }
                    }
                  }}
                />
              </div>
              {selectedMerchant !== 'all' && analytics.vues.evolution.length > 0 ? (
                <p className="text-[11px] text-gray-400 mt-2" data-testid="analytics-sa-views-series-sum">
                  Somme série :{' '}
                  {analytics.vues.evolution
                    .reduce(
                      (s, item) =>
                        s + Number(item.vues ?? item.vuesCommercants ?? item.total ?? 0),
                      0
                    )
                    .toLocaleString('fr-FR')}{' '}
                  · Total affiché : {analytics.vues.commercants.toLocaleString('fr-FR')}
                </p>
              ) : null}
            </div>
          )}

          {/* Top commerçants les plus visités - Uniquement pour vue globale */}
          {selectedMerchant === 'all' && analytics.vues.topCommercants.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Commerçants les plus visités</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rang
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commerçant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vues
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {analytics.vues.topCommercants.slice(0, 10).map((item, index) => (
                      <tr
                        key={item.commercantId || index}
                        className="hover:bg-violet-50/70 cursor-pointer focus-within:bg-violet-50/70"
                        tabIndex={item.commercantId ? 0 : undefined}
                        role={item.commercantId ? 'button' : undefined}
                        aria-label={
                          item.commercantId
                            ? `Analyser ${item.commercant?.nom || 'cette boutique'}`
                            : undefined
                        }
                        onClick={() => {
                          if (!item.commercantId) return;
                          setSelectedMerchant(item.commercantId);
                          syncUrl({ commercantId: item.commercantId });
                        }}
                        onKeyDown={(e) => {
                          if (!item.commercantId) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedMerchant(item.commercantId);
                            syncUrl({ commercantId: item.commercantId });
                          }
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {item.commercant?.logo && (
                              <img 
                                className="h-10 w-10 rounded-full object-cover mr-3" 
                                src={item.commercant.logo} 
                                alt={item.commercant?.nom || ''}
                              />
                            )}
                            <span className="text-sm font-medium text-violet-800">
                              {item.commercant?.nom || 'Commerçant inconnu'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.count?.toLocaleString('fr-FR') || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

      {/* Évolution des commandes (volume) */}
      {analytics.commandes.parJour.length > 0 && (
        <div
          id="analytics-section-commandes-evolution"
          className={`card ${sectionClass(activeSection, 'commandes-evolution')}`}
        >
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Évolution des commandes ({periodTitle})
          </h3>
          <div className="h-72">
            <Line
              data={{
                labels: analytics.commandes.parJour.map((item) =>
                  new Date(item.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                ),
                datasets: [
                  {
                    label: 'Commandes',
                    data: analytics.commandes.parJour.map((item) => item.count),
                    borderColor: 'rgb(59, 130, 246)',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    tension: 0.35,
                    fill: true,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true } },
              }}
            />
          </div>
        </div>
      )}

      <div
        id="analytics-section-commandes"
        className={`grid grid-cols-1 gap-6 lg:grid-cols-2 ${sectionClass(activeSection, 'commandes')}`}
      >
        {/* Évolution des revenus */}
        {analytics.revenus.parMois.length > 0 && (
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Évolution du CA net ventes ({periodTitle})
            </h3>
            <div className="h-64">
              <Line
                data={{
                  labels: analytics.revenus.parMois.map((item) => {
                    if (item.mois && String(item.mois).includes('-') && String(item.mois).length >= 10) {
                      return new Date(`${item.mois}T12:00:00`).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                      });
                    }
                    return item.mois;
                  }),
                  datasets: [
                    {
                      label: 'CA net (€)',
                      data: analytics.revenus.parMois.map((item) => item.revenus),
                      borderColor: 'rgb(34, 197, 94)',
                      backgroundColor: 'rgba(34, 197, 94, 0.1)',
                      tension: 0.4,
                      fill: true,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function(value) {
                          return formatCurrency(value);
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Canaux & paiement */}
        {(analytics.commandes.parCanal.length > 0 ||
          analytics.commandes.parPaiement.length > 0) && (
          <div
            id="analytics-section-commandes-repartition"
            className={`card lg:col-span-2 ${sectionClass(activeSection, 'commandes-repartition')}`}
          >
            <h3 className="text-lg font-medium text-gray-900 mb-1">Répartition des commandes</h3>
            <p className="text-sm text-gray-500 mb-2 flex items-center gap-1.5 flex-wrap">
              {selectedMerchant === 'all'
                ? 'Vue globale — canaux / retraits et modes de paiement'
                : 'Boutique sélectionnée — même périmètre que les commandes payées'}
              <AnalyticsInfoHint text={SITE_WEB_SEMANTICS_HINT} />
            </p>
            {analytics._meta &&
            analytics._meta.channelOrders > 0 &&
            analytics._meta.paidOrders > 0 &&
            analytics._meta.channelOrders !== analytics._meta.paidOrders ? (
              <p
                className="text-xs text-amber-800 mb-3"
                data-testid="analytics-sa-channel-mismatch"
              >
                Attention : total canaux ({analytics._meta.channelOrders}) ≠ commandes payées (
                {analytics._meta.paidOrders})
                {selectedMerchant === 'all'
                  ? ' — le dashboard global compte les commandes non annulées ; les KPI haut utilisent le volume payé.'
                  : '. Vérifiez le filtre.'}
              </p>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {analytics.commandes.parCanal.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Canal / retrait</h4>
                  <div className="h-52">
                    <Doughnut
                      key={`canal-${selectedMerchant}-${period}-${analytics.commandes.parCanal.map((c) => c.commandes).join('-')}`}
                      data={{
                        labels: analytics.commandes.parCanal.map((c) => c.label),
                        datasets: [
                          {
                            data: analytics.commandes.parCanal.map((c) => c.commandes),
                            backgroundColor: CHART_COLORS,
                            borderWidth: 2,
                            borderColor: '#fff',
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { position: 'bottom' } },
                      }}
                    />
                  </div>
                  <ul className="mt-3 space-y-1 text-sm text-gray-600">
                    {analytics.commandes.parCanal.map((c) => (
                      <li key={c.key} className="flex justify-between gap-2">
                        <span>{c.label}</span>
                        <span className="font-medium text-gray-900">
                          {c.commandes} cmd · {formatCurrency(c.ca)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-gray-400 mt-2">
                    Total canaux :{' '}
                    {analytics.commandes.parCanal.reduce((s, c) => s + Number(c.commandes || 0), 0)}
                  </p>
                </div>
              )}
              {analytics.commandes.parPaiement.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Paiement</h4>
                  <div className="h-52">
                    <Doughnut
                      key={`pay-${selectedMerchant}-${period}-${analytics.commandes.parPaiement.map((p) => p.commandes).join('-')}`}
                      data={{
                        labels: analytics.commandes.parPaiement.map((p) => p.label),
                        datasets: [
                          {
                            data: analytics.commandes.parPaiement.map((p) => p.commandes),
                            backgroundColor: CHART_COLORS.slice(2),
                            borderWidth: 2,
                            borderColor: '#fff',
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { position: 'bottom' } },
                      }}
                    />
                  </div>
                  <ul className="mt-3 space-y-1 text-sm text-gray-600">
                    {analytics.commandes.parPaiement.map((p) => (
                      <li key={p.key} className="flex justify-between gap-2">
                        <span>{p.label}</span>
                        <span className="font-medium text-gray-900">
                          {p.commandes} cmd · {formatCurrency(p.ca)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-gray-400 mt-2">
                    Total paiements :{' '}
                    {analytics.commandes.parPaiement.reduce((s, p) => s + Number(p.commandes || 0), 0)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Top performers */}
      {analytics.produits.topVendus.length > 0 && (
        <div
          id="analytics-section-produits"
          className={`card ${sectionClass(activeSection, 'produits')}`}
        >
          <h3 className="text-lg font-medium text-gray-900 mb-1">Top produits</h3>
          <p className="text-sm text-gray-500 mb-4">Quantités vendues et chiffre d&apos;affaires sur la période</p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">#</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Produit</th>
                  {selectedMerchant === 'all' && (
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Boutique</th>
                  )}
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Ventes</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">CA</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Part CA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {analytics.produits.topVendus.slice(0, 10).map((produit, index) => (
                  <tr key={`${produit.nom}-${index}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-400">{index + 1}</td>
                    <td className="px-4 py-2 font-medium text-gray-900">{produit.nom}</td>
                    {selectedMerchant === 'all' && (
                      <td className="px-4 py-2 text-gray-600">{produit.boutique || '—'}</td>
                    )}
                    <td className="px-4 py-2 text-right text-gray-900">{produit.ventes}</td>
                    <td className="px-4 py-2 text-right text-gray-900">{formatCurrency(produit.revenus)}</td>
                    <td className="px-4 py-2 text-right text-gray-600">
                      {produit.partCaPct > 0 ? `${produit.partCaPct}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AnalyticsSystemSection />
    </div>
  );
};

export default Analytics;
